import axios from "axios";
import React, { useEffect, useRef, useState } from "react";

export interface WeatherData {
  location: string;
  temperature: number;
  description: string;
  icon: string;
  humidity: number;
  windSpeed: number;
  date: string;
}

export interface WeatherForecast {
  current: WeatherData;
  forecast: WeatherData[];
}

const API_KEY = process.env.REACT_APP_WEATHER_API_KEY;
const BASE_URL = "https://api.openweathermap.org/data/2.5";

export const getWeatherByCoordinates = async (
  lat: number,
  lon: number
): Promise<WeatherData> => {
  try {
    const response = await axios.get(`${BASE_URL}/weather`, {
      params: {
        lat,
        lon,
        appid: API_KEY,
        units: "metric",
        lang: "en",
      },
    });

    const data = response.data;

    return {
      location: data.name,
      temperature: data.main.temp,
      description: data.weather[0].description,
      icon: data.weather[0].icon,
      humidity: data.main.humidity,
      windSpeed: data.wind.speed,
      date: new Date().toISOString(),
    };
  } catch (error) {
    console.error("Error fetching weather data:", error);
    throw error;
  }
};

export const getWeatherForecast = async (
  lat: number,
  lon: number
): Promise<WeatherForecast> => {
  try {
    // Get current weather
    const current = await getWeatherByCoordinates(lat, lon);

    // Get 5-day forecast
    const forecastResponse = await axios.get(`${BASE_URL}/forecast`, {
      params: {
        lat,
        lon,
        appid: API_KEY,
        units: "metric",
        lang: "en",
        cnt: 40, // 40 time points (5 days, every 3 hours)
      },
    });

    const forecastData = forecastResponse.data;

    // Mapping forecast data
    const forecast = forecastData.list.map((item: any) => ({
      location: forecastData.city.name,
      temperature: item.main.temp,
      description: item.weather[0].description,
      icon: item.weather[0].icon,
      humidity: item.main.humidity,
      windSpeed: item.wind.speed,
      date: item.dt_txt,
    }));

    return {
      current,
      forecast,
    };
  } catch (error) {
    console.error("Error fetching weather forecast:", error);
    throw error;
  }
};

// Weather overlay component for the map
// Weather layer types
export enum WeatherLayerType {
  PRECIPITATION = "precipitation_new",
  CLOUDS = "clouds_new",
  TEMPERATURE = "temp_new",
  WIND = "wind_new",
  PRESSURE = "pressure_new",
}

// Layer options type
export interface WeatherLayerOptions {
  type: WeatherLayerType;
  isVisible: boolean;
}

// Forecast time point interface
export interface ForecastTimePoint {
  timestamp: number; // UNIX timestamp
  label: string; // Display label (e.g., "12h")
}

// Function to get weather forecast for the next period
export const getForecastTimePoints = (): ForecastTimePoint[] => {
  const now = new Date();
  // Round to nearest hour
  now.setMinutes(0, 0, 0);

  const timePoints: ForecastTimePoint[] = [];

  // Current time
  timePoints.push({
    timestamp: now.getTime(),
    label: "Now",
  });

  // 12 time points ahead (every 3 hours = 36 hours total)
  for (let i = 1; i <= 12; i++) {
    const time = new Date(now.getTime() + i * 3 * 60 * 60 * 1000); // Every 3 hours
    timePoints.push({
      timestamp: time.getTime(),
      label: `+${i * 3}h`,
    });
  }

  return timePoints;
};

// Enhanced weather overlay component
export const WeatherMapOverlay: React.FC<{
  map: google.maps.Map | null;
  layerOptions: WeatherLayerOptions[];
  isAnimating: boolean;
  currentTimeIndex: number;
  onTimeIndexChange: (index: number) => void;
}> = ({
  map,
  layerOptions,
  isAnimating,
  currentTimeIndex,
  onTimeIndexChange,
}) => {
  const [weatherLayers, setWeatherLayers] = useState<
    google.maps.ImageMapType[]
  >([]);
  const [forecastTimes, setForecastTimes] = useState<ForecastTimePoint[]>([]);
  const [currentLayer, setCurrentLayer] = useState<WeatherLayerType | null>(
    null
  );
  const animationRef = useRef<number | null>(null);

  // Initialize time points
  useEffect(() => {
    setForecastTimes(getForecastTimePoints());
  }, []);

  // Create layers when layerOptions change
  useEffect(() => {
    if (!map) return;

    // Clear previous layers
    while (map.overlayMapTypes.getLength() > 0) {
      map.overlayMapTypes.pop();
    }

    // Find first visible layer
    const visibleLayer = layerOptions.find((layer) => layer.isVisible);
    if (!visibleLayer) {
      setWeatherLayers([]);
      setCurrentLayer(null);
      return;
    }

    setCurrentLayer(visibleLayer.type);

    // Create layers for different forecast time points
    const newLayers: google.maps.ImageMapType[] = [];

    forecastTimes.forEach((timePoint) => {
      // Convert timestamp to UTC format expected by OpenWeatherMap
      const date = new Date(timePoint.timestamp);
      const utcTime = Math.floor(date.getTime() / 1000);

      const weatherMapType = new google.maps.ImageMapType({
        getTileUrl: function (coord, zoom) {
          // Add current time in milliseconds to avoid caching
          const timestamp = Math.floor(Date.now() / 1000);
          const layer =
            visibleLayer.type === WeatherLayerType.PRECIPITATION
              ? "precipitation_new"
              : visibleLayer.type === WeatherLayerType.CLOUDS
              ? "clouds_new"
              : visibleLayer.type === WeatherLayerType.TEMPERATURE
              ? "temp_new"
              : visibleLayer.type === WeatherLayerType.WIND
              ? "wind_new"
              : "pressure_new";

          return `https://tile.openweathermap.org/map/${layer}/${zoom}/${coord.x}/${coord.y}.png?appid=${process.env.REACT_APP_WEATHER_API_KEY}&date=${utcTime}&_=${timestamp}`;
        },
        tileSize: new google.maps.Size(256, 256),
        name: `${visibleLayer.type}_${utcTime}`,
        maxZoom: 18,
        opacity: 0.8, // Increase opacity for better visibility
      });

      newLayers.push(weatherMapType);
    });

    setWeatherLayers(newLayers);

    // Show first layer if exists
    if (newLayers.length > 0) {
      map.overlayMapTypes.push(newLayers[0]);
    }
  }, [map, layerOptions, forecastTimes]);

  // Animate layers through time
  useEffect(() => {
    if (!map || weatherLayers.length === 0 || !currentLayer) return;

    // Stop existing animation
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }

    // If animation is not enabled, show current layer
    if (!isAnimating) {
      // Remove all existing layers
      while (map.overlayMapTypes.getLength() > 0) {
        map.overlayMapTypes.pop();
      }
      // Add current layer
      if (weatherLayers[currentTimeIndex]) {
        map.overlayMapTypes.push(weatherLayers[currentTimeIndex]);
      }
      return;
    }

    let frameCount = 0;
    const FRAMES_PER_STEP = 180; // Increase to 3 seconds per time point (60fps * 3)

    const animateWeather = () => {
      frameCount++;

      if (frameCount >= FRAMES_PER_STEP) {
        frameCount = 0;

        // Remove all existing layers
        while (map.overlayMapTypes.getLength() > 0) {
          map.overlayMapTypes.pop();
        }

        // Calculate next time point index
        const nextTimeIndex = (currentTimeIndex + 1) % weatherLayers.length;

        // Add next layer
        if (weatherLayers[nextTimeIndex]) {
          map.overlayMapTypes.push(weatherLayers[nextTimeIndex]);
        }

        // Update current index
        onTimeIndexChange(nextTimeIndex);
      }

      animationRef.current = requestAnimationFrame(animateWeather);
    };

    animationRef.current = requestAnimationFrame(animateWeather);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [
    map,
    weatherLayers,
    currentTimeIndex,
    isAnimating,
    onTimeIndexChange,
    currentLayer,
  ]);

  return null;
};

// Export current forecast time point for display
export const useCurrentForecastTime = (
  isAnimating: boolean,
  currentTimeIndex: number,
  forecastTimes: ForecastTimePoint[]
): ForecastTimePoint | null => {
  if (
    !isAnimating ||
    forecastTimes.length === 0 ||
    currentTimeIndex >= forecastTimes.length
  ) {
    return null;
  }

  return forecastTimes[currentTimeIndex];
};
